import { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { useKeyAction } from "../../../hooks/useKeyAction";
import { isTopmostModal, registerModal } from "./modalStack";
import type { ModalProps, ModalSize } from "./types";

const MODAL_SIZE_CLASSES: Record<ModalSize, string> = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-4xl",
  full: "max-w-[95vw] max-h-[95vh]",
};

export const Modal = ({
  isOpen,
  onClose,
  children,
  size = "md",
  className,
  disableBackdropClose = false,
  disableEscapeClose = false,
}: ModalProps) => {
  const modalIdRef = useRef(Symbol("modal"));

  const handleBackdropClick = () => {
    if (!disableBackdropClose) {
      onClose();
    }
  };

  const handleEscape = useCallback(() => {
    if (!isTopmostModal(modalIdRef.current)) {
      return;
    }
    onClose();
  }, [onClose]);

  useKeyAction({
    code: "Escape",
    callback: handleEscape,
    options: { isDisabled: disableEscapeClose || !isOpen },
  });

  useEffect(() => {
    if (!isOpen) return;

    return registerModal(modalIdRef.current);
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleBackdropClick}
      />
      <div
        className={clsx(
          "relative bg-white rounded-lg shadow-xl w-full mx-4 p-4",
          MODAL_SIZE_CLASSES[size],
          className,
        )}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
};
